import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslate } from '../hooks/useTranslate';
import { useFileUpload } from '../hooks/useFileUpload';
import UploadArea from './modal/UploadArea';
import DataObjectConfig from './modal/DataObjectConfig';
import { dataDescriptionService } from '../services/dataDescriptionService';
import { useLanguage } from '../contexts/LanguageContext';
import { UI_MESSAGES, DataType } from '../constants';

interface InlineFileUploadPanelProps {
    onUpload: (file: File, dataObjectData?: { name: string; description?: string; columns?: any[] }) => Promise<{ jobId?: string; dataObjectName?: string; dataObjectUuid?: string } | void> | void;
    onClose?: () => void; // optional parent close
    constrainHeight?: boolean; // when true, use flex layout and scroll only column table
    onStateChange?: (state: {
        selectedFile: File | null;
        analyzing: boolean;
        uploading: boolean;
        canSubmit: boolean;
        handleSubmit: () => Promise<void> | void;
        handleReset: () => void;
        jobId?: string;
        dataObjectName?: string;
        dataObjectUuid?: string;
        uploadComplete?: boolean;
    }) => void;
    suppressInternalFooter?: boolean; // when true, do not render internal FooterActions
}

// Lightweight inline version of FileUploadModal for use inside AddDataSourceModal
// (Add mode only – exclude analyse/replace paths & cancel overlay complexity)
const InlineFileUploadPanel: React.FC<InlineFileUploadPanelProps> = ({ onUpload, onClose, constrainHeight, onStateChange, suppressInternalFooter }) => {
    const { t } = useTranslate();
    const { activeLanguage } = useLanguage();
    const [dataObjectName, setDataObjectName] = useState('');
    const [dataObjectDescription, setDataObjectDescription] = useState('');
    const [nameError, setNameError] = useState('');
    const [autoNameAssigned, setAutoNameAssigned] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dataDescriptionAnalysis, setDataDescriptionAnalysis] = useState<any>(null);
    const [dataDescriptionLoading, setDataDescriptionLoading] = useState(false);
    const [metadataReady, setMetadataReady] = useState(false);
    const [selectedColumnsForReport, setSelectedColumnsForReport] = useState<string[]>([]);
    const autoDescriptionAssignedRef = useRef(false);
    const nameInputRef = useRef<HTMLInputElement | null>(null);
    const descriptionRef = useRef<HTMLTextAreaElement | null>(null);
    const lastUserFocusedRef = useRef<HTMLElement | null>(null);

    const {
        dragActive,
        selectedFile,
        analyzing,
        fileAnalysis,
        showColumnTable,
        fileInputRef,
        handleDrag,
        handleDrop,
        handleFileSelect,
        openFileDialog,
        reset,
        updateColumn,
        analyzeFileAutomatically
    } = useFileUpload();

    // Auto-populate name from file
    useEffect(() => {
        if (selectedFile && !autoNameAssigned) {
            const baseName = selectedFile.name.replace(/\.[^.]+$/, '');
            setDataObjectName(baseName);
            setAutoNameAssigned(true);
            autoDescriptionAssignedRef.current = false; // Reset description flag for new file
        }
    }, [selectedFile, autoNameAssigned]);

    // Trigger analysis automatically on file selection
    useEffect(() => {
        if (selectedFile && !analyzing && !fileAnalysis) {
            analyzeFileAutomatically(selectedFile);
        }
    }, [selectedFile, analyzing, fileAnalysis, analyzeFileAutomatically]);

    // Run description service (best-effort)
    useEffect(() => {
        if (!selectedFile || dataDescriptionLoading || dataDescriptionAnalysis) return;
        const run = async () => {
            setDataDescriptionLoading(true);
            try {
                const analysis = await dataDescriptionService.analyzeFile(selectedFile, activeLanguage);
                setDataDescriptionAnalysis(analysis);
                // Only set description if we haven't already auto-assigned one and it's currently empty
                if (analysis.description && !autoDescriptionAssignedRef.current && !dataObjectDescription.trim()) {
                    setDataObjectDescription(analysis.description);
                    autoDescriptionAssignedRef.current = true;
                }
            } catch {
                // Ignore failures – non-blocking
            } finally {
                setDataDescriptionLoading(false);
            }
        };
        run();
    }, [selectedFile, dataDescriptionLoading, dataDescriptionAnalysis, dataObjectDescription]);

    // Set metadata ready when column table is shown and not analyzing
    useEffect(() => {
        setMetadataReady(showColumnTable && !analyzing && !dataDescriptionLoading);
    }, [showColumnTable, analyzing, dataDescriptionLoading]);

    const onNameChange = useCallback((value: string) => {
        setDataObjectName(value);
        if (nameError && value.trim()) setNameError('');
    }, [nameError]);

    const getMergedAnalysis = useCallback(() => {
        if (!fileAnalysis) return null;

        // If no API analysis yet, return original file analysis with all columns
        if (!dataDescriptionAnalysis) return fileAnalysis;

        const mapApiDataType = (apiType: string): DataType => {
            switch (apiType.toLowerCase()) {
                case 'string': case 'text': case 'varchar': case 'char': return 'String';
                case 'integer': case 'int': case 'bigint': case 'smallint': case 'tinyint': case 'long': return 'Integer';
                case 'float': case 'double': case 'decimal': case 'numeric': case 'number': case 'real': return 'Float';
                case 'boolean': case 'bool': return 'Boolean';
                case 'date': return 'Date';
                case 'timestamp': case 'datetime': case 'time': return 'Timestamp';
                case 'array': case 'map': case 'struct': case 'binary': return 'Complex';
                case 'json': case 'object': return 'String';
                default: return 'String';
            }
        };

        // CRITICAL: Always preserve ALL columns from original file analysis
        // Only enhance with API data where available, never remove columns
        const mergedColumns = fileAnalysis.columns.map(col => {
            const apiCol = dataDescriptionAnalysis.columns?.find((c: any) => c.name === col.name);
            if (apiCol) {
                return {
                    ...col,
                    description: col.description || apiCol.description || '',
                    detectedType: mapApiDataType(apiCol.dataType) || col.detectedType,
                    sampleValues: apiCol.sampleValues?.length ? apiCol.sampleValues : col.sampleValues,
                    hasQualityIssues: apiCol.hasQualityIssues
                };
            }
            // Keep original column even if API doesn't have analysis for it
            return col;
        });

        return { ...fileAnalysis, columns: mergedColumns };
    }, [fileAnalysis, dataDescriptionAnalysis]);

    const validate = useCallback(() => {
        if (!dataObjectName.trim()) {
            setNameError(t('Data object name is required'));
            return false;
        }
        return true;
    }, [dataObjectName]);

    const handleSubmit = useCallback(async () => {
        if (!selectedFile) return;
        if (!validate()) return;
        setUploading(true);
        try {
            const merged = getMergedAnalysis() || fileAnalysis;
            const dataObjectData = {
                name: dataObjectName.trim(),
                description: dataObjectDescription.trim(),
                columns: (merged?.columns || []).map((col, idx) => {
                    const mapType = (frontendType: string): string => {
                        switch (frontendType.toLowerCase()) {
                            case 'boolean': return 'boolean';
                            case 'number': return col.name.toLowerCase().includes('id') ? 'integer' : 'float';
                            case 'date': return 'date';
                            case 'string': default: return 'string';
                        }
                    };
                    const backendType = mapType(col.selectedType || col.detectedType || 'string');
                    // Set analysis_flag based on user's column selection
                    // If user selected specific columns, only those get true; otherwise all default to true
                    const analysisFlag = selectedColumnsForReport.length > 0
                        ? selectedColumnsForReport.includes(col.name)
                        : true;
                    return {
                        name: col.name,
                        logical_type: backendType,
                        nullable: true,
                        description: col.description || '',
                        user_type: backendType,
                        overwrite: false,
                        ordinal_position: idx,
                        sample_values: col.sampleValues || [],
                        rules: col.rules, // ✅ Include DQ rules for deferred creation
                        analysis_flag: analysisFlag,
                        has_quality_issues: col.hasQualityIssues ?? false
                    };
                })
            };
            const uploadResult = await onUpload(selectedFile, dataObjectData);

            // If upload returns jobId info, notify parent for async processing
            if (uploadResult && uploadResult.jobId) {
                if (onStateChange) {
                    // Mark that we've sent jobId info to prevent future useEffect from overwriting it
                    jobIdSentRef.current = true;

                    // Notify with jobId info for async processing
                    onStateChange({
                        selectedFile,
                        analyzing: false, // Sync analysis is done
                        uploading: false, // Upload just completed
                        canSubmit: false, // Reset after upload
                        handleSubmit,
                        handleReset,
                        jobId: uploadResult.jobId,
                        dataObjectName: dataObjectData.name,
                        dataObjectUuid: uploadResult.dataObjectUuid,
                        uploadComplete: true
                    });
                }
                // Don't reset state for async uploads - let the async progress take over
                return;
            }

            // For sync uploads only, reset state after successful upload
            reset();
            setDataObjectName('');
            setDataObjectDescription('');
            setAutoNameAssigned(false);
            setDataDescriptionAnalysis(null);
            setSelectedColumnsForReport([]);
            autoDescriptionAssignedRef.current = false;
            if (onClose) onClose();
        } catch (e) {
            console.error('Inline upload failed', e);
            alert(UI_MESSAGES.UPLOAD.UPLOAD_FAILED);
        } finally {
            setUploading(false);
        }
    }, [selectedFile, dataObjectName, dataObjectDescription, onUpload, reset, getMergedAnalysis, validate, selectedColumnsForReport]);

    const handleReset = useCallback(() => {
        reset();
        setDataObjectName('');
        setDataObjectDescription('');
        setNameError('');
        setAutoNameAssigned(false);
        setDataDescriptionAnalysis(null);
        setSelectedColumnsForReport([]);
        autoDescriptionAssignedRef.current = false;
    }, [reset]);

    // Add ref to track if we've sent jobId info to prevent overwriting it
    const jobIdSentRef = useRef(false);

    // Propagate state upward for modal-level footer rendering
    useEffect(() => {
        // Don't send regular state updates if we've already sent jobId info (async mode active)
        if (onStateChange && !jobIdSentRef.current) {
            onStateChange({
                selectedFile,
                analyzing,
                uploading,
                canSubmit: !!selectedFile && showColumnTable && !analyzing && !uploading && !!dataObjectName.trim() && metadataReady,
                handleSubmit,
                handleReset
            });
        }
    }, [selectedFile, analyzing, uploading, dataObjectName, showColumnTable, metadataReady, handleSubmit, handleReset]);

    // Compute form validity for DataObjectConfig component
    const canSubmit = !!selectedFile && showColumnTable && !analyzing && !uploading && !!dataObjectName.trim() && metadataReady;

    return (
        <div className={constrainHeight ? 'flex flex-col h-full' : 'space-y-6'}>
            {/* Priority 1: File selection area when no file */}
            {!selectedFile && (
                <UploadArea
                    dragActive={dragActive}
                    analyzing={analyzing}
                    onDrag={handleDrag}
                    onDrop={handleDrop}
                    onFileSelect={handleFileSelect}
                    openFileDialog={openFileDialog}
                    fileInputRef={fileInputRef}
                />
            )}
            {/* Priority 2: Analysis loading states */}
            {selectedFile && (analyzing || dataDescriptionLoading) && (
                <div className="dd-loading-container flex flex-col items-center justify-center min-h-96" role="status" aria-live="polite">
                    <span style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem' }}>{t('Analyzing Metadata')}</span>
                    <div className="heartbeat-loader" style={{ maxWidth: '400px', height: '120px' }}>
                        <svg viewBox="0 0 550 206" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 100 L80 100 L90 60 L100 140 L110 40 L120 160 L130 100 L200 100 L210 80 L220 120 L230 70 L240 130 L250 100 L320 100 L330 65 L340 135 L350 50 L360 150 L370 100 L440 100 L450 75 L460 125 L470 55 L480 145 L490 100 L540 100"
                                fill="none"
                                stroke="white"
                                strokeWidth="6"
                                strokeLinecap="round"
                                strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>
            )}
            {/* Priority 3: Column configuration screen when analysis complete - using shared component */}
            {selectedFile && showColumnTable && !analyzing && !dataDescriptionLoading && fileAnalysis && fileAnalysis.columns?.length > 0 && (
                <div className={constrainHeight ? 'flex-1 flex flex-col h-full' : 'h-96 flex flex-col'}>
                    <DataObjectConfig
                        dataObjectName={dataObjectName}
                        dataObjectDescription={dataObjectDescription}
                        nameError={nameError}
                        onNameChange={onNameChange}
                        onDescriptionChange={setDataObjectDescription}
                        nameInputRef={nameInputRef}
                        descriptionRef={descriptionRef}
                        lastUserFocusedRef={lastUserFocusedRef}
                        analysis={getMergedAnalysis() || fileAnalysis}
                        onColumnUpdate={updateColumn}
                        isValid={canSubmit}
                        isProcessing={uploading}
                        onSubmit={handleSubmit}
                        submitLabel={t('Generate Report')}
                        onReset={suppressInternalFooter ? undefined : handleReset}
                        resetLabel={t('Reset')}
                        showFooter={!suppressInternalFooter}
                        onSelectionChange={(selected) => setSelectedColumnsForReport(selected)}
                        initialSelectedColumns={selectedColumnsForReport}
                    />
                </div>
            )}
        </div>
    );
};

export default InlineFileUploadPanel;